import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, ActivityIndicator, RefreshControl } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';

/**
 * Refresh Health Monitor Tab
 *
 * Reads from:
 *   _refresh_health      - latest_TIER1, latest_TIER2, latest_TIER3A, latest_TIER3B,
 *                          latest_{Sport}_PlayerProps, latest_{Sport}_Derivatives
 *   _refresh_retry_queue - main_refresh, tiered_TIER2, tiered_TIER3A, tiered_TIER3B,
 *                          playerProps_{sport}
 *
 * Shows:
 *   - Per-tier "last run" timestamp + staleness badge
 *   - Timeout flag, leagues skipped/completed
 *   - Active retry queues with skipped league lists
 *   - Player props coverage per sport
 *   - Derivative coverage per sport
 */

const TIERS = [
  { key: 'TIER1',  label: 'Tier 1 (51 books)',  schedule: 'Every 15 min', icon: 'flash-outline',      color: '#22c55e' },
  { key: 'TIER2',  label: 'Tier 2 (104 books)', schedule: 'Hourly :18',   icon: 'layers-outline',     color: '#3b82f6' },
  { key: 'TIER3A', label: 'Tier 3A (~65 books)', schedule: 'Hourly :33',  icon: 'layers-outline',     color: '#a855f7' },
  { key: 'TIER3B', label: 'Tier 3B (~65 books)', schedule: 'Hourly :48',  icon: 'layers-outline',     color: '#ec4899' },
];

const SPORTS_PROPS = [
  { key: 'Basketball', label: 'NBA/NCAAB', icon: 'basketball-outline' },
  { key: 'Football',   label: 'NFL/NCAAF', icon: 'american-football-outline' },
  { key: 'Hockey',     label: 'NHL',       icon: 'snow-outline' },
  { key: 'Baseball',   label: 'MLB',       icon: 'baseball-outline' },
  { key: 'Soccer',     label: 'Soccer',    icon: 'football-outline' },
  { key: 'Tennis',     label: 'Tennis',    icon: 'tennisball-outline' },
  { key: 'Golf',       label: 'Golf',      icon: 'golf-outline' },
  { key: 'Motorsports',label: 'F1/NASCAR', icon: 'car-sport-outline' },
  { key: 'OtherSports',label: 'Other',     icon: 'ellipsis-horizontal-outline' },
];

const RETRY_KEYS = [
  { key: 'main_refresh',  label: 'Main (Tier 1)' },
  { key: 'tiered_TIER2',  label: 'Tier 2' },
  { key: 'tiered_TIER3A', label: 'Tier 3A' },
  { key: 'tiered_TIER3B', label: 'Tier 3B' },
];

function ageMs(ts) {
  if (!ts) return null;
  const ms = ts?.toMillis?.() || (typeof ts === 'string' ? new Date(ts).getTime() : ts?.seconds ? ts.seconds * 1000 : null);
  if (!ms) return null;
  return Date.now() - ms;
}

function formatAge(ms) {
  if (ms === null) return 'never';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m ago`;
}

function stalenessColor(ms, maxOkMs) {
  if (ms === null) return '#ef4444';
  if (ms < maxOkMs) return '#22c55e';
  if (ms < maxOkMs * 2) return '#f59e0b';
  return '#ef4444';
}

const RefreshHealthTab = () => {
  const [tierData, setTierData] = useState({});
  const [propsData, setPropsData] = useState({});
  const [derivData, setDerivData] = useState({});
  const [retryData, setRetryData] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState({ tiers: true, props: true, deriv: false, retries: true });
  const [now, setNow] = useState(Date.now());

  // Tick every 30s so relative timestamps update
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const loadAll = useCallback(async () => {
    try {
      // Tiers
      const tierResults = {};
      await Promise.all(TIERS.map(async (t) => {
        try {
          const snap = await getDoc(doc(db, '_refresh_health', `latest_${t.key}`));
          tierResults[t.key] = snap.exists() ? snap.data() : null;
        } catch { tierResults[t.key] = null; }
      }));
      setTierData(tierResults);

      // Player props per sport
      const propsResults = {};
      await Promise.all(SPORTS_PROPS.map(async (s) => {
        try {
          const snap = await getDoc(doc(db, '_refresh_health', `latest_${s.key}_PlayerProps`));
          propsResults[s.key] = snap.exists() ? snap.data() : null;
        } catch { propsResults[s.key] = null; }
      }));
      setPropsData(propsResults);

      // Derivatives per sport
      const derivResults = {};
      await Promise.all(SPORTS_PROPS.map(async (s) => {
        try {
          const snap = await getDoc(doc(db, '_refresh_health', `latest_${s.key}_Derivatives`));
          derivResults[s.key] = snap.exists() ? snap.data() : null;
        } catch { derivResults[s.key] = null; }
      }));
      setDerivData(derivResults);

      // Retry queues
      const retryResults = {};
      await Promise.all(RETRY_KEYS.map(async (r) => {
        try {
          const snap = await getDoc(doc(db, '_refresh_retry_queue', r.key));
          retryResults[r.key] = snap.exists() ? snap.data() : null;
        } catch { retryResults[r.key] = null; }
      }));
      setRetryData(retryResults);
    } catch (e) {
      console.error('[RefreshHealth] load error:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const onRefresh = () => { setRefreshing(true); loadAll(); };

  const toggle = (section) => setExpanded(prev => ({ ...prev, [section]: !prev[section] }));

  if (loading) {
    return (
      <View style={s.center}>
        <ActivityIndicator size="large" color="#6366f1" />
        <Text style={s.loadingText}>Loading refresh health...</Text>
      </View>
    );
  }

  // Count issues
  const timedOutCount = TIERS.filter(t => tierData[t.key]?.timedOut).length;
  const staleCount = TIERS.filter(t => {
    const ms = ageMs(tierData[t.key]?.timestamp || tierData[t.key]?.updatedAt);
    const maxOk = t.key === 'TIER1' ? 20 * 60000 : 90 * 60000;
    return ms === null || ms > maxOk;
  }).length;
  const activeRetries = RETRY_KEYS.filter(r => retryData[r.key] && !retryData[r.key].resolved).length;

  const overallOk = timedOutCount === 0 && staleCount === 0 && activeRetries === 0;

  return (
    <ScrollView
      style={s.container}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#6366f1" />}
    >
      {/* Header */}
      <View style={s.header}>
        <View style={s.headerLeft}>
          <Ionicons name="pulse-outline" size={22} color="#6366f1" />
          <Text style={s.headerTitle}>Refresh Health Monitor</Text>
        </View>
        <TouchableOpacity style={s.refreshBtn} onPress={onRefresh}>
          <Ionicons name="refresh-outline" size={16} color="#6366f1" />
          <Text style={s.refreshBtnText}>Refresh</Text>
        </TouchableOpacity>
      </View>

      {/* Overall status banner */}
      <View style={[s.banner, { borderColor: overallOk ? '#22c55e' : '#ef4444', backgroundColor: overallOk ? '#052e1633' : '#3b000a33' }]}>
        <Ionicons
          name={overallOk ? 'checkmark-circle' : 'warning'}
          size={20}
          color={overallOk ? '#22c55e' : '#ef4444'}
        />
        <View style={s.bannerText}>
          <Text style={[s.bannerTitle, { color: overallOk ? '#22c55e' : '#ef4444' }]}>
            {overallOk ? 'All Systems Normal' : `${timedOutCount + staleCount + activeRetries} Issue(s) Detected`}
          </Text>
          {!overallOk && (
            <Text style={s.bannerSub}>
              {[
                timedOutCount > 0 && `${timedOutCount} tier(s) timed out`,
                staleCount > 0 && `${staleCount} tier(s) stale`,
                activeRetries > 0 && `${activeRetries} active retry queue(s)`,
              ].filter(Boolean).join(' · ')}
            </Text>
          )}
        </View>
      </View>

      {/* ── Tier Refresh Status ─────────────────────────────────────── */}
      <SectionHeader
        title="Tier Refresh Status"
        icon="layers-outline"
        open={expanded.tiers}
        onToggle={() => toggle('tiers')}
      />
      {expanded.tiers && (
        <View style={s.section}>
          {TIERS.map(tier => {
            const d = tierData[tier.key];
            const ts = d?.timestamp || d?.updatedAt;
            const ms = ageMs(ts);
            const maxOk = tier.key === 'TIER1' ? 20 * 60000 : 90 * 60000;
            const color = stalenessColor(ms, maxOk);
            const completed = d?.leaguesCompleted ?? 0;
            const total = d?.totalLeagues ?? 0;
            const skipped = d?.leaguesSkipped ?? 0;
            const timedOut = d?.timedOut ?? false;
            const duration = d?.durationSeconds != null ? `${d.durationSeconds.toFixed(0)}s` : null;
            const books = d?.sportsbookCount ?? null;

            return (
              <View key={tier.key} style={s.tierCard}>
                <View style={s.tierHeader}>
                  <View style={[s.tierDot, { backgroundColor: tier.color }]} />
                  <View style={s.tierInfo}>
                    <Text style={s.tierLabel}>{tier.label}</Text>
                    <Text style={s.tierSchedule}>{tier.schedule}</Text>
                  </View>
                  <View style={s.tierRight}>
                    <View style={[s.ageBadge, { backgroundColor: color + '22', borderColor: color }]}>
                      <Text style={[s.ageBadgeText, { color }]}>{formatAge(ms)}</Text>
                    </View>
                  </View>
                </View>

                {d ? (
                  <View style={s.tierStats}>
                    <StatPill label="Leagues" value={total > 0 ? `${completed}/${total}` : `${completed}`} color={completed === total && total > 0 ? '#22c55e' : '#f59e0b'} />
                    {skipped > 0 && <StatPill label="Skipped" value={skipped} color="#f59e0b" />}
                    {books && <StatPill label="Books" value={books} color="#6366f1" />}
                    {duration && <StatPill label="Duration" value={duration} color="#94a3b8" />}
                    {timedOut && <StatPill label="TIMED OUT" value="!" color="#ef4444" />}
                  </View>
                ) : (
                  <Text style={s.noData}>No data yet</Text>
                )}

                {d?.leaguesSkippedList?.length > 0 && (
                  <View style={s.skippedList}>
                    <Text style={s.skippedListTitle}>Skipped: </Text>
                    <Text style={s.skippedListText}>{d.leaguesSkippedList.slice(0, 20).join(', ')}{d.leaguesSkippedList.length > 20 ? ` +${d.leaguesSkippedList.length - 20} more` : ''}</Text>
                  </View>
                )}
              </View>
            );
          })}
        </View>
      )}

      {/* ── Retry Queues ────────────────────────────────────────────── */}
      <SectionHeader
        title={`Retry Queues ${activeRetries > 0 ? `(${activeRetries} active)` : '(clear)'}`}
        icon="refresh-circle-outline"
        open={expanded.retries}
        onToggle={() => toggle('retries')}
        badgeCount={activeRetries}
      />
      {expanded.retries && (
        <View style={s.section}>
          {RETRY_KEYS.map(r => {
            const d = retryData[r.key];
            if (!d) {
              return (
                <View key={r.key} style={[s.retryCard, s.retryCardClear]}>
                  <Ionicons name="checkmark-circle-outline" size={14} color="#22c55e" />
                  <Text style={[s.retryCardLabel, { color: '#22c55e', marginLeft: 6 }]}>{r.label} — clear</Text>
                </View>
              );
            }
            const ms = ageMs(d.timestamp);
            const leagues = d.skippedLeagues || [];
            const retryCount = d.retryCount ?? 0;
            const isExpired = ms !== null && ms > 2 * 60 * 60 * 1000;

            return (
              <View key={r.key} style={[s.retryCard, { borderColor: isExpired ? '#64748b' : '#f59e0b' }]}>
                <View style={s.retryCardHeader}>
                  <Ionicons name="alert-circle-outline" size={14} color={isExpired ? '#64748b' : '#f59e0b'} />
                  <Text style={[s.retryCardLabel, { color: isExpired ? '#64748b' : '#f59e0b' }]}>
                    {r.label} — {leagues.length} league{leagues.length !== 1 ? 's' : ''} queued
                  </Text>
                  <Text style={s.retryAge}>{formatAge(ms)} · attempt {retryCount}/3{isExpired ? ' · EXPIRED' : ''}</Text>
                </View>
                {leagues.length > 0 && (
                  <Text style={s.retryLeagues}>{leagues.slice(0, 15).join(', ')}{leagues.length > 15 ? ` +${leagues.length - 15} more` : ''}</Text>
                )}
              </View>
            );
          })}
        </View>
      )}

      {/* ── Player Props Coverage ───────────────────────────────────── */}
      <SectionHeader
        title="Player Props Coverage"
        icon="person-outline"
        open={expanded.props}
        onToggle={() => toggle('props')}
      />
      {expanded.props && (
        <View style={s.section}>
          <View style={s.sportGrid}>
            {SPORTS_PROPS.map(sp => (
              <SportCard
                key={sp.key}
                sport={sp}
                data={propsData[sp.key]}
                maxOkMs={75 * 60000}
                valueKey="propsStored"
                valueLabel="props"
              />
            ))}
          </View>
        </View>
      )}

      {/* ── Derivative Coverage ─────────────────────────────────────── */}
      <SectionHeader
        title="Derivative Markets Coverage"
        icon="bar-chart-outline"
        open={expanded.deriv}
        onToggle={() => toggle('deriv')}
      />
      {expanded.deriv && (
        <View style={s.section}>
          <View style={s.sportGrid}>
            {SPORTS_PROPS.map(sp => (
              <SportCard
                key={sp.key}
                sport={sp}
                data={derivData[sp.key]}
                maxOkMs={75 * 60000}
                valueKey="marketsStored"
                valueLabel="markets"
              />
            ))}
          </View>
        </View>
      )}

      <View style={{ height: 40 }} />
    </ScrollView>
  );
};

/* ── Sub-components ────────────────────────────────────────────────────────── */

const SectionHeader = ({ title, icon, open, onToggle, badgeCount }) => (
  <TouchableOpacity style={s.sectionHeader} onPress={onToggle} activeOpacity={0.7}>
    <Ionicons name={icon} size={16} color="#94a3b8" />
    <Text style={s.sectionTitle}>{title}</Text>
    {badgeCount > 0 && (
      <View style={s.badge}><Text style={s.badgeText}>{badgeCount}</Text></View>
    )}
    <Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={14} color="#475569" style={{ marginLeft: 'auto' }} />
  </TouchableOpacity>
);

const StatPill = ({ label, value, color }) => (
  <View style={[s.statPill, { borderColor: color + '44', backgroundColor: color + '15' }]}>
    <Text style={[s.statPillText, { color }]}>{label}: {value}</Text>
  </View>
);

const SportCard = ({ sport, data, maxOkMs, valueKey, valueLabel }) => {
  const ts = data?.timestamp || data?.updatedAt;
  const ms = ageMs(ts);
  const color = stalenessColor(ms, maxOkMs);
  const timedOut = data?.timedOut ?? false;
  const value = data?.[valueKey] ?? null;
  const books = data?.sportsbookCount ?? null;

  return (
    <View style={[s.sportCard, { borderColor: color + '55' }]}>
      <View style={s.sportCardHeader}>
        <Ionicons name={sport.icon} size={14} color={color} />
        <Text style={s.sportCardLabel}>{sport.label}</Text>
        {timedOut && <Ionicons name="warning-outline" size={12} color="#f59e0b" style={{ marginLeft: 'auto' }} />}
      </View>
      <Text style={[s.sportCardAge, { color }]}>{formatAge(ms)}</Text>
      {value !== null && (
        <Text style={s.sportCardValue}>{value.toLocaleString()} {valueLabel}</Text>
      )}
      {books !== null && (
        <Text style={s.sportCardBooks}>{books} books</Text>
      )}
      {!data && <Text style={s.sportCardNoData}>No data</Text>}
    </View>
  );
};

/* ── Styles ────────────────────────────────────────────────────────────────── */

const s = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0f172a',
  },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    backgroundColor: '#0f172a',
  },
  loadingText: {
    color: '#94a3b8',
    marginTop: 12,
    fontSize: 14,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    paddingBottom: 8,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#f1f5f9',
  },
  refreshBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#6366f133',
    backgroundColor: '#6366f111',
  },
  refreshBtnText: {
    color: '#6366f1',
    fontSize: 13,
    fontWeight: '600',
  },
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginHorizontal: 16,
    marginBottom: 12,
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
  },
  bannerText: {
    flex: 1,
  },
  bannerTitle: {
    fontSize: 14,
    fontWeight: '700',
  },
  bannerSub: {
    fontSize: 12,
    color: '#94a3b8',
    marginTop: 2,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: '#1e293b',
    borderTopWidth: 1,
    borderTopColor: '#334155',
    marginTop: 4,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#cbd5e1',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  badge: {
    backgroundColor: '#ef4444',
    borderRadius: 10,
    minWidth: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  badgeText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '700',
  },
  section: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 8,
  },
  tierCard: {
    backgroundColor: '#1e293b',
    borderRadius: 10,
    padding: 12,
    borderWidth: 1,
    borderColor: '#334155',
    gap: 8,
  },
  tierHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  tierDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  tierInfo: {
    flex: 1,
  },
  tierLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#f1f5f9',
  },
  tierSchedule: {
    fontSize: 11,
    color: '#64748b',
    marginTop: 1,
  },
  tierRight: {
    alignItems: 'flex-end',
  },
  ageBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    borderWidth: 1,
  },
  ageBadgeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  tierStats: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  statPill: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 5,
    borderWidth: 1,
  },
  statPillText: {
    fontSize: 11,
    fontWeight: '600',
  },
  noData: {
    fontSize: 12,
    color: '#475569',
    fontStyle: 'italic',
  },
  skippedList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    borderTopWidth: 1,
    borderTopColor: '#334155',
    paddingTop: 8,
    marginTop: 2,
  },
  skippedListTitle: {
    fontSize: 11,
    color: '#f59e0b',
    fontWeight: '600',
  },
  skippedListText: {
    fontSize: 11,
    color: '#94a3b8',
    flex: 1,
  },
  retryCard: {
    backgroundColor: '#1e293b',
    borderRadius: 8,
    padding: 10,
    borderWidth: 1,
    gap: 4,
  },
  retryCardClear: {
    borderColor: '#22c55e33',
    backgroundColor: '#052e1622',
    flexDirection: 'row',
    alignItems: 'center',
  },
  retryCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  retryCardLabel: {
    fontSize: 13,
    fontWeight: '600',
  },
  retryAge: {
    fontSize: 11,
    color: '#64748b',
    marginLeft: 'auto',
  },
  retryLeagues: {
    fontSize: 11,
    color: '#94a3b8',
    marginTop: 2,
  },
  sportGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  sportCard: {
    width: '48%',
    backgroundColor: '#1e293b',
    borderRadius: 8,
    padding: 10,
    borderWidth: 1,
    gap: 3,
  },
  sportCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  sportCardLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#e2e8f0',
  },
  sportCardAge: {
    fontSize: 12,
    fontWeight: '600',
    marginTop: 2,
  },
  sportCardValue: {
    fontSize: 11,
    color: '#94a3b8',
  },
  sportCardBooks: {
    fontSize: 10,
    color: '#475569',
  },
  sportCardNoData: {
    fontSize: 11,
    color: '#475569',
    fontStyle: 'italic',
  },
});

export default RefreshHealthTab;
